import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle,Mail, Phone, MapPin, ChevronDown, Sparkles, Zap, Factory, Globe } from 'lucide-react';
import './homecss.css';
import img1 from './assets/images/img1.jpeg';
import img2 from './assets/images/img2.jpeg';
import img3 from './assets/images/img3.jpeg';
import IMG5 from './assets/images/IMG5.png';
import IMG6 from './assets/images/IMG6.png';
import IMG7 from './assets/images/IMG 7.png';


import nirmaanlogo from './assets/images/nirmaan logo.png';

export default function BatteryWebsite() {
  const [activeSection, setActiveSection] = useState('home');
  const [scrollY, setScrollY] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [chargeLevel, setChargeLevel] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    setIsLoaded(true);
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const maxScroll = contentRef.current.scrollHeight - window.innerHeight;
    const percentage = Math.min(100, Math.max(0, (scrollY / maxScroll) * 100));

    setChargeLevel(Math.floor(percentage));
  }, [scrollY]);

  const scrollToSection = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(section);
    }
  };

  const particleCount = 30;
  const particles = Array.from({ length: particleCount }, (_, i) => ({
    id: i,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
    left: Math.random() * 100,
  }));

  return (
    <div className="battery-site" ref={contentRef}>
      {/* FIXED CENTER BATTERY INDICATOR */}
      <div className="battery-charge-wrapper">
        <div className="battery-shell">
          <div className="battery-fill" style={{ height: `${chargeLevel}%` }} />
        </div>
        <div className="battery-percent">{chargeLevel}%</div>
      </div>

      {/* Cursor Glow Effect */}
      <div
        className="cursor-glow"
        style={{
          left: `${mousePos.x}px`,
          top: `${mousePos.y}px`,
          opacity: isLoaded ? 1 : 0
        }}
      />

      {/* Animated Background Particles */}
      <div className="bg-particles">
        {particles.map(p => (
          <div
            key={p.id}
            className="particle"
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header>
        <div className="header-content">
          <div className="logo">
            <img src={nirmaanlogo} alt="MITO-Z Logo" className="logo-image" />
            <span className="logo-text">MITO-Z</span>
          </div>
          <nav>
            <ul className="nav-links">
              <li
                className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}
                onClick={() => scrollToSection('home')}
              >
                Home
              </li>
              <li
                className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
                onClick={() => scrollToSection('about')}
              >
                About
              </li>
              <li
                className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                onClick={() => scrollToSection('contact')}
              >
                Contact
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Home Section */}
      <section id="home">
        <div className="section-content">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Powering zinc-ion battery
            </div>
            <h1>The Future of Energy Storage</h1>
            <p className="subtitle">
              Mito-Z is developing a safer, lower-cost hybrid zinc-ion alternative to replace lead-acid batteries for stationary energy storage.
            </p>
            <div className="cta-buttons">
              <button className="btn btn-primary" onClick={() => scrollToSection('about')}>
                Discover More
              </button>
              <button className="btn btn-secondary" onClick={() => scrollToSection('contact')}>
                Get In Touch
              </button>
            </div>

            <div className="battery-visual">
              <div className="battery-card">
                <div className="stat-number">2x</div>
                <div className="stat-label">Longer Lifespan than Lead-Acid battery</div>
              </div>
              <div className="battery-card">
                <div className="stat-number">25%</div>
                <div className="stat-label">Less cost than Lead-Acid battery</div>
              </div>
              <div className="battery-card">
                <div className="stat-number">2x</div>
                <div className="stat-label">Better performance than Lead-Acid battery</div>
              </div>
            </div>
          </div>
          <div className="scroll-indicator">
            <ChevronDown size={32} color="#00d4ff" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about">
        <div className="section-content">
          <h2 className="section-title">Why MITO-Z?</h2>
          <p className="section-subtitle">
            Mito-Z is built by a technically strong, interdisciplinary team with deep expertise in battery chemistry, cell and pack design, BMS, and automation. Backed by NIRMAAN and embedded within the IIT Madras ecosystem, we have access to world-class research facilities, mentorship from leading scientists, and the IITM Research Park for collaboration and scale-up. With a clear path to IP creation, a large lead-acid replacement market, and a capital-efficient route to commercialization, Mito-Z is positioned to translate deep-tech research into a scalable, India's first zinc-ion energy storage company.
          </p>

          {/* Interactive Image Gallery */}
          
          <div className="image-gallery">
            <div className="gallery-item" data-index="0">
              <div className="gallery-image-wrapper">
                <img src={IMG5} alt="Manufacturing Compatibility" />
                <div className="gallery-overlay">
                  <div className="overlay-icon">
                    <Factory size={40} />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="gallery-item" data-index="1">
              <div className="gallery-image-wrapper">
                <img src={img2} alt="Manufacturing Compatibility" />
                <div className="gallery-overlay">
                  <div className="overlay-icon">
                    <Factory size={40} />
                  </div>
                </div>
              </div>
            </div>
            <div className="gallery-item" data-index="2">
              <div className="gallery-image-wrapper">
                <img src={img3} alt="Indigenous supply chain" />
                <div className="gallery-overlay">
                  <div className="overlay-icon">
                    <Globe size={40} />
                  </div>
                </div>
              </div>
            </div>
            <div className="gallery-item" data-index="3">
              <div className="gallery-image-wrapper">
                <img src={IMG6} alt="Manufacturing Compatibility" />
                <div className="gallery-overlay">
                  <div className="overlay-icon">
                    <Factory size={40} />
                  </div>
                </div>
              </div>
            </div><div className="gallery-item" data-index="4">
              <div className="gallery-image-wrapper">
                <img src={IMG7} alt="Manufacturing Compatibility" />
                <div className="gallery-overlay">
                  <div className="overlay-icon">
                    <Factory size={40} />
                  </div>
                </div>
              </div>
            </div>
            
          
          <div className="gallery-item" data-index="5">
              <div className="gallery-image-wrapper">
                <img src={img1} alt="Unique cell design" />
                <div className="gallery-overlay">
                  <div className="overlay-icon">
                    <Zap size={40} />
                  </div>
                </div>
              </div>
            </div>
          </div>


          <div className="about-grid">
            <div className="feature-card">
              <div className="feature-icon icon-electric">
                <Zap size={40} color="white" />
              </div>
              <h3 className="feature-title">Unique cell design</h3>
              <p className="feature-description">
                Traditional zinc-ion cell designs are fundamentally cost-limited, which is why lead-acid continues to win on price; Mito-Z overcomes this by redesigning the cell architecture to achieve lead-acid–competitive cost enabling zinc-ion batteries to replace lead-acid demand.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon icon-factory">
                <Factory size={40} color="white" />
              </div>
              <h3 className="feature-title">Manufacturing Compatibility</h3>
              <p className="feature-description">
                Mito-Z is designed as a drop-in solution, allowing existing lead-acid manufacturing lines to be adapted for zinc-ion production, enabling rapid and low-capital scale-up.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon icon-globe">
                <Globe size={40} color="white" />
              </div>
              <h3 className="feature-title">Indigenous supply chain</h3>
              <p className="feature-description">
                Zinc-ion batteries use raw materials that are cheaper than lead-acid and fully available in India, enabling a truly domestic, Make-in-India supply chain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact">
        <div className="section-content">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Ready to revolutionize your energy storage? Let's talk about how MITO-Z can power your future.
          </p>

          <div className="contact-container">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={24} color="#ffffff" />
                </div>
                <div className="contact-details">
                  <h3>Email Us</h3>
                  <p>info@mitoz.in</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={24} color="#ffffff" />
                </div>
                <div className="contact-details">
                  <h3>Call Us</h3>
                  <p>+91 (8883121897)</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <MapPin size={24} color="#ffffff" />
                </div>
                <div className="contact-details">
                  <h3>Visit Us</h3>
                  <p>Nirmaan, Sudha Shankar Innovation Hub, Indian Institute of Technology Madras, Chennai 600036.</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">
                  <MessageCircle size={24} color="#ffffff" />
                </div>
                <div className="contact-details">
                  <h3>Whatsapp Us</h3>
                  <p>+91 (8883121897)</p>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = e.target.name.value;
                  const email = e.target.email.value;
                  const message = e.target.message.value;

                  const mailtoLink = `mailto:info@mitoz.in?subject=MITO-Z Inquiry from ${encodeURIComponent(name)}&body=${encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`)}`;

                  window.location.href = mailtoLink;
                }}
              >
                <p className="form-note">
                  We currently accept inquiries via email. Your message will open in your email app for submission.
                </p>

                <div className="form-group">
                  <label>Name</label>
                  <input type="text" name="name" placeholder="Your name" required />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" placeholder="your@email.com" required />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea name="message" placeholder="Tell us about your project..." required></textarea>
                </div>

                <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                  Submit Inquiry
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-logo">MITO-Z</div>
          <p className="footer-text">
            © 2026 MITO-Z Battery Technologies. Powering Tomorrow's World.
          </p>
        </div>
      </footer>
    </div>
  );
}